import Vue from 'vue';

import store from '@/store';

import { CURRENT_HOST_EVENT_TRACKING_OPTIONS, CURRENT_HOST_REGION, CURRENT_HOST_SENSORS_AVAILABLE } from './map';

import { requestEventTracking, requestEventTrackingAvailableList } from './api';

import { hex_md5 } from 'react-native-md5';

import { getDeviceId } from './browser-fingerprint';

/**
 * 自定义事件埋点映射
 *
 * @description 自定义事件埋点，不通过神策埋点上报，单独接口上报。
 *
 * @type {Map<string, object>}
 */
const CUSTOM_EVENT_TRACKING_MAP = new Map();

const initSensors = async () => {
  const sensorID = CURRENT_HOST_EVENT_TRACKING_OPTIONS.sensorsID;
  const sensorsURL = CURRENT_HOST_EVENT_TRACKING_OPTIONS.sensorsURL;
  const sensorsName = CURRENT_HOST_EVENT_TRACKING_OPTIONS.sensorsName;

  /**
   * @see https://manual.sensorsdata.cn/sa/docs/tech_sdk_client_web_use/v0300
   */
  const sensors = (window['sensors'] = window[sensorID]);

  const server_url = new URL(sensorsURL);
  server_url.searchParams.set('module', 'yundikan');
  server_url.searchParams.set('project', sensorsName);

  /**
   * 初始化神策
   */
  sensors?.init({
    /**
     * 数据接收地址
     */
    server_url: server_url.href,
    use_client_time: true,
    /**
     * 表示使用 beacon 请求方式发数据，可选使用 'image' 图片 get 请求方式发数据。( 神策系统 1.10 版本以后 ) 支持使用 'ajax' 和 'beacon' 方式发送数据，这两种默认都是 post 方式， beacon 方式兼容性较差。
     */
    send_type: 'image',
    /**
     * 表示是否开启单页面自动采集 $pageview 功能，SDK 会在 url 改变之后自动采集 web 页面浏览事件 $pageview。
     */
    is_track_single_page: true,
    /**
     * 设置 true 后会在网页控制台打 logger，会显示发送的数据,设置 false 表示不显示。
     */
    show_log: false,
    /**
     * 点击图配置。默认配置表示不自动采集元素点击事件和页面停留事件，配置成 {} 表示开启 $WebClick 采集 和 $WebStay 自动采集，默认 $WebClick 只采集 a，button，input ，textarea 四个 dom 元素的点击事件。
     */
    heatmap: {
      // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
      clickmap: 'default',
      //是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
      scroll_notice_map: 'default'
    }
  });

  /**
   * 用于采集 $pageview 事件。
   */
  sensors?.quick('autoTrack');

  /**
   * 用户信息为空，则请求刷新用户信息
   */
  if (!store.state.User.userInfo) {
    try {
      await store.dispatch('User/getUserInfo');
    } finally {
      //
    }
  }

  sensors?.login(store.state.User.userInfo?.accountId || '');
  sensors?.setProfile({
    ...store.state.User.userInfo,
    project: sensorsName
  });
};

/**
 * 初始化埋点
 * @param { Vue } vue - Vue 实例
 * @returns { void }
 */
export async function install(_vue) {
  // if (__APP_BRAND__ === 'lysora') {
  //   window.sensorsEvent = () => {};
  //   window.sensors = {
  //     init: () => {},
  //     login: () => {},
  //     logout: () => {},
  //     quick: () => {},
  //     track: () => {},
  //     setProfile: () => {}
  //   };
  //   return;
  // }

  let deviceId = null;

  window.sensorsEvent = async (name, params) => {
    if (__DEV__ || __TEST__) {
      console.log(`sensors:::${CURRENT_HOST_REGION}:::`, name, params);
    }

    if (CUSTOM_EVENT_TRACKING_MAP.has(name) && store.state.User.userInfo) {
      console.log(`custom_event_tracking:::${name}:::`, CUSTOM_EVENT_TRACKING_MAP.get(name));

      const userId = store.state.User.userInfo?.accountId ?? '';

      requestEventTracking({
        staId: deviceId,
        eMd5: hex_md5(`reas${userId}${name}${Date.now()}`),
        eOrder: 1,
        eList: [
          {
            userId,
            eName: name,
            ts: Date.now(),
            tz: new Date().getTimezoneOffset(),
            mainSn: 0
          }
        ]
      });
    }

    if (!CURRENT_HOST_SENSORS_AVAILABLE) {
      return;
    }

    // debugger;

    window.sensors?.track(name, {
      ...params,
      continent: CURRENT_HOST_REGION
    });
  };

  try {
    deviceId = await getDeviceId();
  } catch (error) {
    console.log(error);
  }

  const userIsLoggedIn = store.state.User.hasLogin || JSON.parse(sessionStorage.getItem('hasLogin'));

  if (userIsLoggedIn) {
    try {
      const data = await requestEventTrackingAvailableList();

      data.forEach(item => {
        item.eventName && CUSTOM_EVENT_TRACKING_MAP.set(item.eventName, item);
      });
    } catch (error) {
      console.log(error);
    } finally {
      //
    }
  }

  if (CURRENT_HOST_SENSORS_AVAILABLE) {
    console.log('Sensors available, initializing...');

    initSensors();
    return;
  }

  window.sensors = {
    init: () => {},
    login: () => {},
    logout: () => {},
    quick: () => {},
    track: () => {},
    setProfile: () => {}
  };
}

export default {
  install
};
